import React from 'react';

import {
  IconFileTypeXls,
  IconFileTypePdf,
  IconPhotoDown,
  IconFileTypeCsv,
  IconFileTypeTxt,
} from '@tabler/icons-react';
import { Tooltip } from 'react-tooltip';

interface DownloadDocumentButtonBaseProps {
  fileType: 'excel' | 'pdf' | 'image' | 'csv' | 'txt';
  tooltipText?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  dataTooltipId?: string;
  disabled?: boolean;
}

type DownloadDocumentButtonProps = DownloadDocumentButtonBaseProps &
  ({ downloadUrl: string; onClick?: never } | { onClick: () => void; downloadUrl?: never });

export const DownloadDocumentButton: React.FC<DownloadDocumentButtonProps> = ({
  downloadUrl,
  onClick,
  fileType,
  tooltipText,
  tooltipPosition = 'left',
  disabled = false,
  dataTooltipId = 'download',
  className,
}) => {
  const renderIcon = () => {
    switch (fileType) {
      case 'excel':
        return <IconFileTypeXls stroke={2} />;
      case 'pdf':
        return <IconFileTypePdf stroke={2} />;
      case 'image':
        return <IconPhotoDown stroke={2} />;
      case 'csv':
        return <IconFileTypeCsv stroke={2} />;
      case 'txt':
        return <IconFileTypeTxt stroke={2} />;
      default:
        return null;
    }
  };

  const disabledClassName = disabled
    ? 'cursor-not-allowed'
    : 'hover:border-pink-500 hover:bg-gray-900';

  const baseClassName = `bg-transparent flex items-center justify-center rounded-md border border-transparent-0 p-2 text-white transition-all duration-300  ${disabledClassName} ${className}`;

  const accessibleName = tooltipText || `Download ${fileType}`;

  const tooltip = tooltipText && !disabled && (
    <Tooltip id={`${dataTooltipId}-${fileType}`} className="max-w-[300px]" place={tooltipPosition}>
      {tooltipText}
    </Tooltip>
  );

  if (downloadUrl) {
    return (
      <a
        href={disabled ? undefined : downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={accessibleName}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        data-tooltip-id={`${dataTooltipId}-${fileType}`}
        className={`${baseClassName}${disabled ? ' pointer-events-none' : ''}`}
        onClick={(e) => {
          if (disabled) e.preventDefault();
        }}
      >
        {renderIcon()}
        {tooltip}
      </a>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={accessibleName}
      data-tooltip-id={`${dataTooltipId}-${fileType}`}
      className={baseClassName}
      onClick={() => onClick?.()}
    >
      {renderIcon()}
      {tooltip}
    </button>
  );
};
